import { LitElement, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';
import { SELECTORS } from '../../constants/selectors';

/**
 * @element kd-header-nav
 * @description グローバルナビのドロップダウンメニューを制御する。
 * ホバーで開閉し、キーボード操作（Enter/Space/Escape/ArrowDown）にも対応。
 */
@customElement('kd-header-nav')
export class KdHeaderNav extends LitElement {
  createRenderRoot() {
    return this;
  }

  private items: HTMLElement[] = [];

  private closeTimers = new WeakMap<HTMLElement, ReturnType<typeof setTimeout>>();

  private static readonly CLOSE_DELAY = 80;

  render() {
    return nothing;
  }

  connectedCallback(): void {
    super.connectedCallback();

    this.items = Array.from(
      this.querySelectorAll<HTMLElement>(SELECTORS.HEADER.NAV_ITEM),
    );

    for (const item of this.items) {
      const trigger = item.querySelector<HTMLElement>(
        SELECTORS.HEADER.NAV_TRIGGER,
      );
      if (trigger) {
        item.addEventListener('mouseenter', () => this.handleMouseEnter(item));
        item.addEventListener('mouseleave', () => this.handleMouseLeave(item));

        trigger.addEventListener('keydown', (e: KeyboardEvent) => {
          this.handleTriggerKeydown(e, item);
        });
      }
    }

    document.addEventListener('keydown', this.handleDocumentKeydown);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    document.removeEventListener('keydown', this.handleDocumentKeydown);
  }

  private handleMouseEnter(item: HTMLElement): void {
    const timer = this.closeTimers.get(item);
    if (timer) {
      clearTimeout(timer);
      this.closeTimers.delete(item);
    }

    for (const other of this.items) {
      if (other !== item) this.close(other);
    }
    this.open(item);
  }

  private handleMouseLeave(item: HTMLElement): void {
    const timer = setTimeout(() => {
      this.close(item);
      this.closeTimers.delete(item);
    }, KdHeaderNav.CLOSE_DELAY);
    this.closeTimers.set(item, timer);
  }

  private handleTriggerKeydown(e: KeyboardEvent, item: HTMLElement): void {
    switch (e.key) {
      case 'Enter':
      case ' ':
        e.preventDefault();
        this.toggle(item);
        break;
      case 'Escape':
        this.close(item);
        (
          item.querySelector(SELECTORS.HEADER.NAV_TRIGGER) as HTMLElement
        )?.focus();
        break;
      case 'ArrowDown': {
        e.preventDefault();
        const dropdown = item.querySelector(SELECTORS.HEADER.NAV_DROPDOWN);
        const firstLink = dropdown?.querySelector<HTMLElement>('a');
        if (firstLink) {
          this.open(item);
          firstLink.focus();
        }
        break;
      }
      default:
        break;
    }
  }

  private handleDocumentKeydown = (e: KeyboardEvent): void => {
    if (e.key !== 'Escape') return;
    for (const item of this.items) {
      if (
        item
          .querySelector(SELECTORS.HEADER.NAV_TRIGGER)
          ?.getAttribute('aria-expanded') === 'true'
      ) {
        this.close(item);
      }
    }
  };

  private open(item: HTMLElement): void {
    item
      .querySelector(SELECTORS.HEADER.NAV_TRIGGER)
      ?.setAttribute('aria-expanded', 'true');
  }

  private close(item: HTMLElement): void {
    item
      .querySelector(SELECTORS.HEADER.NAV_TRIGGER)
      ?.setAttribute('aria-expanded', 'false');
  }

  private toggle(item: HTMLElement): void {
    const trigger = item.querySelector(SELECTORS.HEADER.NAV_TRIGGER);
    const isOpen = trigger?.getAttribute('aria-expanded') === 'true';
    if (isOpen) {
      this.close(item);
    } else {
      for (const other of this.items) {
        if (other !== item) this.close(other);
      }
      this.open(item);
    }
  }
}
